/**
 * Üst çubuktaki kur göstergesi — Blazor `ExchangeRateWidget.razor` karşılığı.
 *
 * Çubukta yalnız okunması gereken iki şey var: USD ve EUR satış kuru. Eski tasarım
 * bunların yanına renkli birer madalyon, iki satırlık tarih ve çerçeve koyuyordu;
 * gösterge başlığın yarısını kaplıyor, sayılar ise küçük kalıyordu. Şimdi sayı önde,
 * geri kalan her şey (alış, diğer birimler, tarih) TIKLANINCA açılan kartta.
 *
 * Değişim oku, kurun BİR ÖNCEKİ KAYDA göre farkı. Kayıtlar her gün girilmiyor
 * (ölçülen kurulumda iki kayıt arasında aylar var), bu yüzden "günlük değişim"
 * denmiyor; karşılaştırılan tarih kartta yazılı.
 *
 * Tazeleme: Merkez Bankası kuru öğleden sonra yayımlıyor. Zamanlayıcı yerine sorgu
 * 15 dakikada bir bayatlıyor ve pencere odağa gelince tazeleniyor.
 */
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { RefreshCw, TrendingDown, TrendingUp } from 'lucide-react';
import { apiClient } from '@/shared/api/http/client';
import { ENDPOINTS } from '@/shared/api/endpoints';
import { queryKeys } from '@/shared/api/queryKeys';
import { formatNumber } from '@/shared/lib/octo/formatters';
import { usePrefsStore } from '@/entities/preferences/model/prefsStore';
import { cn } from '@/shared/lib/cn';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/Popover';
import { IconButton } from '@/shared/ui/IconButton';

interface CurrencyUnit {
  exchangeRateCurrencyUnitId: number;
  currencyCode: string | null;
  currencyName: string | null;
  currencyBuyRate: number | null;
  currencySellRate: number | null;
}
interface ExchangeRateDto {
  exchangeRateDate?: string | null;
  currencyUnits?: CurrencyUnit[] | null;
}

/** Üst çubukta yalnız bu ikisi; kalanı karttaki listede. */
const SHOWN = ['USD', 'EUR'];

/** Para birimi simgesi — kod yerine simge okunuyor, kod zaten ipucunda. */
const SYMBOL: Record<string, string> = { USD: '$', EUR: '€', GBP: '£', TRY: '₺' };

/**
 * Gösterilecek kurlar: yalnız USD ve EUR, USD önce (web ile aynı sıra). Satış kuru
 * olmayan birim atlanır — kutuda "-" göstermek kur varmış izlenimi veriyor.
 */
export function pickHeaderRates(units: CurrencyUnit[] | null | undefined): CurrencyUnit[] {
  return (units ?? [])
    .filter((u) => u.currencyCode && SHOWN.includes(u.currencyCode) && u.currencySellRate != null)
    .sort((a, b) => SHOWN.indexOf(a.currencyCode!) - SHOWN.indexOf(b.currencyCode!));
}

/**
 * Önceki kayda göre yüzde değişim. Önceki kayıt ya da kur yoksa null döner ve ok
 * hiç çizilmez — "%0,00" göstermek, değişim ölçülmüş gibi bir izlenim verirdi.
 */
export function changePercent(current: number | null | undefined, previous: number | null | undefined) {
  if (current == null || previous == null || previous === 0) return null;
  return ((current - previous) / previous) * 100;
}

export function ExchangeRateBadge({ className }: { className?: string }) {
  const { t } = useTranslation();
  const locale = usePrefsStore((s) => s.prefs.language);
  const q = useQuery({
    queryKey: queryKeys.misc.exchangeRates(),
    // İki kayıt: bugünkü kur ve karşılaştırma için bir önceki.
    queryFn: () =>
      apiClient.get<ExchangeRateDto[]>(ENDPOINTS.EXCHANGE_RATES_RECENT(2), {
        octo: { expectSuccess: false },
      }),
    staleTime: 15 * 60_000,
    refetchOnWindowFocus: true,
  });

  const records = Array.isArray(q.data) ? q.data : q.data ? [q.data] : [];
  const record = records[0];
  const previous = records[1];
  const rows = pickHeaderRates(record?.currencyUnits);

  // Veri yoksa gösterge hiç çizilmez: "-" dolu boş bir kutu üst çubukta yer kaplıyor.
  if (rows.length === 0) return null;

  const date = record?.exchangeRateDate ? new Date(record.exchangeRateDate) : null;
  const previousDate = previous?.exchangeRateDate ? new Date(previous.exchangeRateDate) : null;
  const previousOf = (code: string | null) =>
    previous?.currencyUnits?.find((u) => u.currencyCode === code)?.currencySellRate ?? null;

  const dateText = date ? date.toLocaleDateString(locale, { day: '2-digit', month: 'short' }) : '';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="rate-badge"
          aria-label={t('shell.rates.title', { date: date ? date.toLocaleDateString(locale) : '' })}
          className={cn(
            'hover:bg-bg-hover focus-visible:ring-ring hidden items-center gap-3 rounded-lg px-2 py-1 transition-colors focus-visible:ring-2 focus-visible:outline-none md:flex',
            className,
          )}
        >
          {rows.map((r) => {
            const change = changePercent(r.currencySellRate, previousOf(r.currencyCode));
            return (
              <span key={r.exchangeRateCurrencyUnitId} className="flex items-baseline gap-1.5">
                <span className="text-fg-subtle text-sm leading-none font-medium" aria-hidden="true">
                  {SYMBOL[r.currencyCode!] ?? r.currencyCode}
                </span>
                <span className="text-sm leading-none font-semibold tabular-nums">
                  {formatNumber(r.currencySellRate, 4)}
                </span>
                {/* Değişim yalnız geniş ekranda: dar başlıkta sayıların okunurluğunu
                    bozmasın, kartta zaten yazılı. */}
                {change !== null ? (
                  <span
                    className={cn(
                      'hidden text-[11px] leading-none font-medium tabular-nums lg:inline',
                      change >= 0 ? 'text-success' : 'text-danger',
                    )}
                  >
                    {change >= 0 ? '▲' : '▼'}
                    {formatNumber(Math.abs(change), 2)}%
                  </span>
                ) : null}
              </span>
            );
          })}
          {dateText ? (
            <span className="text-fg-subtle hidden text-[11px] leading-none lg:inline">{dateText}</span>
          ) : null}
        </button>
      </PopoverTrigger>

      {/* Ayrıntı kartı: bütün birimler, alış/satış ve karşılaştırma tarihi. */}
      <PopoverContent align="end" className="w-80 p-0">
        <div className="border-border flex items-center justify-between border-b px-3 py-2">
          <div className="min-w-0">
            <div className="text-sm font-semibold">{t('shell.rates.heading')}</div>
            {date ? (
              <div className="text-fg-subtle text-[11px]">
                {date.toLocaleDateString(locale, { day: '2-digit', month: 'long', year: 'numeric' })}
              </div>
            ) : null}
          </div>
          <IconButton
            label={t('common:Refresh')}
            size="sm"
            onClick={() => void q.refetch()}
            disabled={q.isFetching}
          >
            <RefreshCw className={cn('h-4 w-4', q.isFetching && 'animate-spin')} />
          </IconButton>
        </div>

        <table className="w-full text-sm">
          <thead className="text-fg-subtle text-[11px]">
            <tr>
              <th className="px-3 py-1 text-left font-medium">{t('shell.rates.currency')}</th>
              <th className="px-2 py-1 text-right font-medium">{t('shell.rates.buy')}</th>
              <th className="px-3 py-1 text-right font-medium">{t('shell.rates.sell')}</th>
            </tr>
          </thead>
          <tbody>
            {(record?.currencyUnits ?? [])
              .filter((u) => u.currencySellRate != null || u.currencyBuyRate != null)
              .map((u) => {
                const change = changePercent(u.currencySellRate, previousOf(u.currencyCode));
                return (
                  <tr key={u.exchangeRateCurrencyUnitId} className="border-border/60 border-t">
                    <td className="px-3 py-1.5">
                      <span className="flex items-center gap-1.5">
                        <span className="text-fg-subtle w-3 text-center">
                          {SYMBOL[u.currencyCode ?? ''] ?? ''}
                        </span>
                        <span className="font-medium">{u.currencyCode}</span>
                        {change !== null ? (
                          <span
                            className={cn(
                              'inline-flex items-center gap-0.5 text-[10px] tabular-nums',
                              change >= 0 ? 'text-success' : 'text-danger',
                            )}
                          >
                            {change >= 0 ? (
                              <TrendingUp className="h-3 w-3" />
                            ) : (
                              <TrendingDown className="h-3 w-3" />
                            )}
                            {formatNumber(Math.abs(change), 2)}%
                          </span>
                        ) : null}
                      </span>
                    </td>
                    <td className="text-fg-muted px-2 py-1.5 text-right tabular-nums">
                      {u.currencyBuyRate != null ? formatNumber(u.currencyBuyRate, 4) : '—'}
                    </td>
                    <td className="px-3 py-1.5 text-right font-medium tabular-nums">
                      {u.currencySellRate != null ? formatNumber(u.currencySellRate, 4) : '—'}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>

        {previousDate ? (
          <div className="border-border text-fg-subtle border-t px-3 py-1.5 text-[11px]">
            {t('shell.rates.comparedTo', {
              date: previousDate.toLocaleDateString(locale, {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
              }),
            })}
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
